import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import UserService from "services/user.service";
import { IUser } from "models/church.types";

export default function UserList() {
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);

  // ÉTATS DE PAGINATION
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    loadInitialData();
  }, [page, size]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // On récupère l'utilisateur connecté et la liste en parallèle pour plus d'efficacité
      const [accountRes, usersRes] = await Promise.all([
        UserService.getCurrentAccount(),
        UserService.getAll(page, size),
      ]);

      setCurrentUser(accountRes.data);
      setUsers(usersRes.data || []);

      const totalCount = usersRes.headers["x-total-count"];
      if (totalCount) {
        setTotalItems(parseInt(totalCount, 10));
      }
    } catch (error) {
      console.error("Erreur d'initialisation :", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await UserService.getAll(page, size);
      setUsers(response.data || []);
      const totalCount = response.headers["x-total-count"];
      if (totalCount) setTotalItems(parseInt(totalCount, 10));
    } catch (error) {
      toast.error("Erreur lors du rafraîchissement");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (user: IUser) => {
    const confirmMessage = user.activated
      ? `Désactiver l'utilisateur ${user.login} ?`
      : `Activer l'utilisateur ${user.login} ?`;

    if (window.confirm(confirmMessage)) {
      try {
        await UserService.changeStatus({
          id: user.id!,
          enable: !user.activated,
        });
        fetchUsers();
        toast.success("Statut mis à jour");
      } catch (error) {
        toast.error("Impossible de modifier le statut");
      }
    }
  };

  const handleDelete = async (user: IUser) => {
    if (
      window.confirm(`Supprimer définitivement l'utilisateur ${user.login} ?`)
    ) {
      try {
        await UserService.delete(user.id!);
        fetchUsers();
        toast.success("Utilisateur supprimé");
      } catch (error) {
        console.error("Erreur lors de la suppression :", error);
        toast.error(
          "Impossible de supprimer car utilisateur concerné par un traitement",
        );
      }
    }
  };

  const totalPages = Math.ceil(totalItems / size);

  return (
    <div className="flex flex-col justify-start w-full min-h-screen px-4 pt-10">
      <div className="relative z-10 flex flex-col md:flex-row gap-4 mt-20 mb-8 px-4 justify-between items-center">
        <Link
          to="/admin/users/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm"
        >
          <i className="fas fa-user-plus mr-2 text-lg"></i> Nouvel Utilisateur
        </Link>
      </div>

      <div className="relative flex flex-col min-w-0 break-words w-full mb-10 shadow-2xl rounded-2xl bg-slate-800 border border-slate-700 overflow-hidden">
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr className="bg-[#0F172A]/50 text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">
                <th className="px-6 py-4 text-left">Identifiant</th>
                <th className="px-6 py-4 text-left">Email</th>
                <th className="px-6 py-4 text-center">Statut</th>
                <th className="px-6 py-4 text-left">Rôles</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {loading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-slate-500 italic"
                  >
                    <i className="fas fa-circle-notch fa-spin mr-2 text-[#D4AF37]"></i>{" "}
                    Chargement...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-slate-500 italic"
                  >
                    Aucun utilisateur trouvé.
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  // LOGIQUE DE PROTECTION : On ne peut pas s'auto-modifier/supprimer
                  const isSelf =
                    currentUser && user.login === currentUser.login;

                  return (
                    <tr
                      key={user.id || user.login}
                      className="hover:bg-slate-700/30 transition-colors group"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3 border ${isSelf ? "bg-[#D4AF37] text-slate-900 border-[#D4AF37]" : "bg-slate-700 text-[#D4AF37] border-slate-600"}`}
                          >
                            {(user.login || "U").charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-bold text-white">
                            {user.login}{" "}
                            {isSelf && (
                              <span className="ml-1 text-[10px] text-[#D4AF37] font-normal">
                                (Moi)
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-400 font-medium">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 text-center text-xs">
                        <span
                          className={`px-3 py-1 rounded-full border font-bold uppercase text-[9px] ${user.activated ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-red-500/10 text-red-500 border-red-500/20"}`}
                        >
                          {user.activated ? "Actif" : "Désactivé"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {user.authorities?.map((auth) => (
                            <span
                              key={auth}
                              className="bg-slate-900 text-[#D4AF37] text-[9px] px-2 py-0.5 rounded border border-[#D4AF37]/20 font-semibold italic"
                            >
                              {auth.replace("ROLE_", "")}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end items-center gap-2">
                          {/* MODIFICATION : Bloquée pour soi-même */}
                          <Link
                            to={isSelf ? "#" : `/admin/users/edit/${user.id}`}
                            onClick={(e) => isSelf && e.preventDefault()}
                            className={`flex items-center justify-center w-9 h-9 rounded-xl transition-all ${isSelf ? "opacity-10 cursor-not-allowed text-slate-500" : "text-slate-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"}`}
                            title={
                              isSelf
                                ? "Utilisez les paramètres de profil pour vous modifier"
                                : "Modifier"
                            }
                          >
                            <i className="fas fa-edit text-sm"></i>
                          </Link>

                          {/* STATUT : Bloqué pour soi-même */}
                          <button
                            onClick={() => handleStatusChange(user)}
                            disabled={isSelf}
                            title={
                              isSelf
                                ? "Vous ne pouvez pas désactiver votre propre compte"
                                : "Changer statut"
                            }
                            className={`flex items-center justify-center w-9 h-9 rounded-xl transition-all ${isSelf ? "opacity-10 cursor-not-allowed" : "text-slate-400 hover:bg-slate-700"}`}
                          >
                            <i
                              className={`fas ${user.activated ? "fa-user-slash" : "fa-user-check"} text-sm`}
                            ></i>
                          </button>

                          {/* SUPPRESSION : Bloquée pour soi-même */}
                          <button
                            onClick={() => handleDelete(user)}
                            disabled={isSelf}
                            title={
                              isSelf
                                ? "Vous ne pouvez pas supprimer votre propre compte"
                                : "Supprimer"
                            }
                            className={`flex items-center justify-center w-9 h-9 rounded-xl transition-all ${isSelf ? "opacity-10 cursor-not-allowed" : "text-slate-400 hover:text-red-500 hover:bg-red-500/10"}`}
                          >
                            <i className="fas fa-trash-alt text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total : <span className="text-[#D4AF37]">{totalItems}</span>{" "}
            utilisateurs
          </p>
          <div className="flex gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage(page + 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
